import { useState } from "react";
import { api, originUrl } from "@/lib/api";
import { toast } from "sonner";
import { useContent } from "@/context/ContentContext";

export default function GiftCards() {
  const c = useContent().pages.gift_cards;
  const presets = String(c.preset_amounts || "").split(",").map((x) => Number(x.trim())).filter((x) => x > 0);
  const minAmt = Number(c.minimum_amount) || 5;
  const [amount, setAmount] = useState(presets[1] || presets[0] || 50);
  const [custom, setCustom] = useState("");
  const [form, setForm] = useState({ buyer_name: "", buyer_email: "", gift_recipient_name: "", gift_recipient_email: "", gift_message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    const amt = custom ? Number(custom) : amount;
    if (!amt || amt < minAmt) return toast.error(`Minimum $${minAmt}`);
    setBusy(true);
    try {
      const { data } = await api.post("/checkout/gift-card", { kind: "gift_card", amount: amt, ...form, origin_url: originUrl() });
      setDone(data);
    } catch { toast.error("Could not submit request"); } finally { setBusy(false); }
  };

  if (done) return (
    <div className="max-w-2xl mx-auto px-6 py-16 text-center" data-testid="gc-pending">
      <div className="bg-white rounded-3xl p-8 shadow-card">
        <h1 className="font-serif text-4xl">Gift card request received!</h1>
        <p className="mt-4 text-charcoal/75">Amount: <b>${Number(done.amount).toFixed(2)}</b>. Complete your payment with Square and we'll send the gift card to {form.gift_recipient_email}.</p>
        {done.square_link ? <a href={done.square_link} target="_blank" rel="noreferrer" data-testid="gc-pay-square" className="mt-8 inline-flex items-center gap-2 rounded-full bg-charcoal text-cream px-8 py-4 font-bold shadow-button">Pay with Square</a>
          : <p className="mt-6 text-sm text-charcoal-muted">We'll email you a secure Square payment link shortly.</p>}
      </div>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
      <div className="max-w-2xl">
        <div className="text-terracotta text-sm tracking-widest uppercase font-medium">{c.eyebrow}</div>
        <h1 className="mt-2 font-serif font-bold text-5xl md:text-6xl leading-tight">{c.heading}</h1>
        <p className="mt-4 text-lg text-charcoal/70">{c.description}</p>
      </div>

      <form onSubmit={submit} className="mt-10 bg-white rounded-3xl p-8 shadow-card" data-testid="gift-card-form">
        <h3 className="font-serif text-2xl font-semibold">Amount</h3>
        <div className="mt-4 grid grid-cols-3 sm:grid-cols-6 gap-3">
          {presets.map((p) => (
            <button key={p} type="button" onClick={() => { setAmount(p); setCustom(""); }}
              data-testid={`gc-preset-${p}`}
              className={`rounded-2xl py-4 font-serif text-xl font-semibold transition-colors ${amount === p && !custom ? "bg-terracotta text-white" : "bg-cream-alt text-charcoal hover:bg-ochre/40"}`}>
              ${p}
            </button>
          ))}
        </div>
        <div className="mt-4">
          <label className="text-sm font-medium">Or custom amount</label>
          <input data-testid="gc-custom" type="number" min={minAmt} value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="$" className="mt-1 w-40 rounded-2xl border border-cream-alt px-4 py-3 focus:ring-2 focus:ring-terracotta focus:outline-none"/>
        </div>

        <h3 className="mt-10 font-serif text-2xl font-semibold">From</h3>
        <div className="mt-4 grid md:grid-cols-2 gap-4">
          <F label="Your Name" v={form.buyer_name} on={(v) => setForm({...form, buyer_name: v})} req name="buyer_name"/>
          <F label="Your Email" v={form.buyer_email} on={(v) => setForm({...form, buyer_email: v})} type="email" req name="buyer_email"/>
        </div>

        <h3 className="mt-10 font-serif text-2xl font-semibold">To</h3>
        <div className="mt-4 grid md:grid-cols-2 gap-4">
          <F label="Recipient Name" v={form.gift_recipient_name} on={(v) => setForm({...form, gift_recipient_name: v})} req name="gift_recipient_name"/>
          <F label="Recipient Email" v={form.gift_recipient_email} on={(v) => setForm({...form, gift_recipient_email: v})} type="email" req name="gift_recipient_email"/>
        </div>
        <div className="mt-4">
          <label className="text-sm font-medium">Personal Message</label>
          <textarea data-testid="gc-message" rows={3} value={form.gift_message} onChange={(e) => setForm({...form, gift_message: e.target.value})} className="mt-1 w-full rounded-2xl border border-cream-alt px-4 py-3 focus:ring-2 focus:ring-terracotta focus:outline-none"/>
        </div>

        <button data-testid="gc-submit" type="submit" disabled={busy} className="mt-8 w-full rounded-full bg-terracotta text-white px-8 py-4 font-semibold shadow-button disabled:opacity-60">
          {busy ? "Submitting…" : `Continue — $${custom || amount}`}
        </button>
        <p className="mt-3 text-xs text-charcoal-muted text-center">{c.footnote}</p>
      </form>
    </div>
  );
}
function F({ label, v, on, type = "text", req, name }) {
  return (
    <div>
      <label className="text-sm font-medium">{label}</label>
      <input data-testid={`gc-${name}`} required={req} type={type} value={v} onChange={(e) => on(e.target.value)}
        className="mt-1 w-full rounded-2xl border border-cream-alt px-4 py-3 focus:ring-2 focus:ring-terracotta focus:outline-none"/>
    </div>
  );
}
